"use client";

import { useEffect, useRef } from "react";

/**
 * Animated stat counter. Takes the FINAL display string (e.g. "24 mois",
 * "10 000+", "98 %"), parses its leading fr-FR number, and counts up from 0
 * over ~1.1s the first time it scrolls into view. Non-numeric prefix/suffix
 * text is kept verbatim.
 *
 * SSR renders the final string (SEO + no-JS correct); the count-up only runs
 * client-side. The number span reserves its final width in `ch` and uses
 * tabular figures, so the animation causes zero layout shift. Reduced motion
 * → static final value.
 */
export function StatCounter({ value, className }: { value: string; className?: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);

  // Split "10 000+" → prefix "", number "10 000", suffix "+".
  const match = /^([^\d]*)([\d\s.,  ]*\d)(.*)$/.exec(value ?? "");
  const prefix = match?.[1] ?? "";
  const numberText = match?.[2] ?? "";
  const suffix = match?.[3] ?? "";
  // "10 000" → 10000 ; "1,5" → 1.5
  const target = numberText ? Number.parseFloat(numberText.replace(/[\s  ]/g, "").replace(",", ".")) : NaN;
  const decimals = /[.,]\d/.test(numberText) ? (numberText.split(/[.,]/)[1] ?? "").length : 0;

  useEffect(() => {
    const el = ref.current;
    if (!el || !Number.isFinite(target)) return;
    if (typeof IntersectionObserver === "undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const format = (n: number) =>
      n.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

    const run = () => {
      const t0 = performance.now();
      const duration = 1100;
      const tick = (now: number) => {
        const p = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = format(target * eased);
        if (p < 1) requestAnimationFrame(tick);
        else el.textContent = numberText; // restore the exact authored string
      };
      requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            run();
            observer.unobserve(el);
          }
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!Number.isFinite(target)) return <span className={className}>{value}</span>;

  return (
    <span className={className}>
      {prefix}
      <span
        ref={ref}
        className="inline-block text-right tabular-nums"
        style={{ minWidth: `${numberText.length}ch` }}
      >
        {numberText}
      </span>
      {suffix}
    </span>
  );
}
